// Q9 S4 — Step-0 visit_type fallback for the VisitReport wizard.
//
// Surfaces ONLY when SR.visit_type is null/undefined at wizard mount
// (Q9 D4 + D6 exception). Coordinator-set visit_type at SR creation
// skips this step entirely. The carer's pick is PATCHed onto the SR
// (not onto the visit-report draft), then the wizard recomputes its
// visibleSteps once and advances to the first revealed section.
//
// Design choice: radio-group, not a dropdown — this is the carer's
// FIRST decision in the flow and the choice cascades into which
// downstream steps render. Radios make all five options visible at
// once + are easier to tap on a phone than a select with 5 hidden
// options.

import { useState } from "react";
import type { JSX } from "react";
import { VISIT_TYPES, type VisitType } from "@hcm/core";

const VISIT_TYPE_LABELS: Record<VisitType, { title: string; subtitle: string }> = {
  standard_wellness:  { title: "Standard wellness",  subtitle: "Routine check-in. Vitals + notes only." },
  extended_care:      { title: "Extended care",      subtitle: "Longer visit; clinical and wellbeing assessment." },
  companion:          { title: "Companion",          subtitle: "Companionship visit; clinical assessment included." },
  post_discharge:     { title: "Post-discharge",     subtitle: "Visit after recent hospital discharge." },
  emergency_followup: { title: "Emergency follow-up", subtitle: "Acute issue or recent escalation." },
};

interface Props {
  /** Optional pre-selection (rare — coordinator usually picks at SR creation). */
  initialValue?: VisitType | null;
  /** Whether the parent is currently PATCHing the SR. Disables the button. */
  isSaving: boolean;
  /** Called when the carer presses Continue. Parent PATCHes SR + recomputes steps. */
  onContinue: (visitType: VisitType) => void;
}

export function VisitTypeStep({ initialValue, isSaving, onContinue }: Props): JSX.Element {
  const [picked, setPicked] = useState<VisitType | null>(initialValue ?? null);

  return (
    <div data-testid="visit-type-step" className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-slate-900">What kind of visit is this?</h2>
        <p className="mt-1 text-sm text-slate-600">
          Pick the visit type — it determines which sections the wizard surfaces.
        </p>
      </div>

      <fieldset className="space-y-2">
        <legend className="sr-only">Visit type</legend>
        {VISIT_TYPES.map((value) => {
          const meta = VISIT_TYPE_LABELS[value];
          const checked = picked === value;
          return (
            <label
              key={value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                checked ? "border-navy-500 bg-navy-50" : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <input
                type="radio"
                name="visit_type"
                value={value}
                checked={checked}
                onChange={() => setPicked(value)}
                className="mt-0.5 text-navy-700"
                data-testid={`visit-type-${value}`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">{meta.title}</p>
                <p className="text-xs text-slate-500">{meta.subtitle}</p>
              </div>
            </label>
          );
        })}
      </fieldset>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={picked === null || isSaving}
          onClick={() => { if (picked) onContinue(picked); }}
          className="px-5 py-2 text-sm font-medium rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="visit-type-continue"
        >
          {isSaving ? "Saving…" : "Continue"}
        </button>
      </div>
    </div>
  );
}
